import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGroupsStore } from '../../store/groups/Groups';
import { useSelectedGroup } from '../../store/groups/SelectedGroup';
import IconLeftMenuUnsorded from '../icons/iconLeftMenu/IconLeftMenuUnsorded';
import IconLeftMenuAllDevice from '../icons/iconLeftMenu/IconLeftMenuAllDevice';

import IconLeftMenuDevice from '../icons/iconLeftMenu/IconLeftMenuDevice';
import { Group } from '../../types/Group';
import { ConfigProvider, Input, Menu } from 'antd';
import ButtonAddPlus from '../buttons/buttonAddPlus/ButtonAddPlus';
import { SearchOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import ButtonSettingGroup from '../buttons/buttonSettingGroup/ButtonSettingGroup';
import NewGroupModal from '../modals/newGroup/NewGroupModal';
import SettingGroupModal from '../modals/settingsGroup/SettingGroupModal';
import './style.css';

const { SubMenu } = Menu;

const SideMenu: React.FC = () => {
    const { groups, fetchGroups } = useGroupsStore();
    const { selectedGroup, setSelectedGroup } = useSelectedGroup();
    const [isModalNewGroupOpen, setIsModalNewGroupOpen] = useState(false);
    const [isModalSettingGroupOpen, setIsModalSettingGroupOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [openKeys, setOpenKeys] = useState<string[]>([]);

    useEffect(() => {
        if (groups.length === 0) {
            fetchGroups();
        }
    }, [groups]);

    const getGroupIcon = (uid: string) => {
        switch (uid) {
            case '00000000-0000-0000-0000-000000000002':
                return <IconLeftMenuUnsorded />;
            case '00000000-0000-0000-0000-000000000003':
                return <IconLeftMenuAllDevice />;
            default:
                return <IconLeftMenuDevice />;
        }
    };

    const flatGroups = useMemo(() => {
        return groups.flatMap((g) => (g.name === 'Root' ? g.sub_groups || [] : [g]));
    }, [groups]);

    const filteredGroups = useMemo(() => {
        return flatGroups.filter(
            (group) =>
                searchText.trim() === '' ||
                group.name.toLowerCase().includes(searchText.toLowerCase()) ||
                group.sub_groups?.some((sub) =>
                    sub.name.toLowerCase().includes(searchText.toLowerCase()),
                ),
        );
    }, [flatGroups, searchText]);

    const handleSettingGroupDrawerClose = useCallback(() => {
        setIsModalSettingGroupOpen(false);
    }, []);

    const generateMenuItems = useCallback((groups: Group[]) => {
        return groups.map((group) => {
            if (!group.sub_groups?.length) {
                return (
                    <Menu.Item
                        className={'body medium-bold'}
                        style={{ color: 'var(--primary)' }}
                        key={group.uid}
                        icon={getGroupIcon(group.uid)}
                    >
                        {group.name}
                    </Menu.Item>
                );
            }

            return (
                <SubMenu
                    key={group.uid}
                    title={
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            {getGroupIcon(group.uid)}
                            <span
                                className={'body medium-bold'}
                                style={{ color: 'var(--primary)', marginLeft: 8 }}
                            >
                                {group.name}
                            </span>
                        </div>
                    }
                    icon={null}
                    onTitleClick={() => setSelectedGroup(group.uid)}
                    className={'body medium-bold'}
                    style={{ color: 'var(--primary)' }}
                >
                    {generateMenuItems(group.sub_groups)}
                </SubMenu>
            );
        });
    }, []);

    const menuItems = useMemo(() => {
        const mainGroups = filteredGroups
            .filter(
                (g) =>
                    ![
                        '00000000-0000-0000-0000-000000000002',
                        '00000000-0000-0000-0000-000000000003',
                    ].includes(g.uid),
            )
            .sort((a, b) => a.name.localeCompare(b.name));

        const allDevicesGroup = filteredGroups.find(
            (g) => g.uid === '00000000-0000-0000-0000-000000000003',
        );
        const unsortedGroup = filteredGroups.find(
            (g) => g.uid === '00000000-0000-0000-0000-000000000002',
        );

        return [
            allDevicesGroup && (
                <Menu.Item
                    className={'body medium-bold'}
                    style={{ color: 'var(--primary)' }}
                    key={allDevicesGroup.uid}
                    icon={getGroupIcon(allDevicesGroup.uid)}
                >
                    {allDevicesGroup.name}
                </Menu.Item>
            ),
            ...generateMenuItems(mainGroups),
            unsortedGroup && (
                <Menu.Item
                    className={'body medium-bold'}
                    style={{ color: 'var(--primary)' }}
                    key={unsortedGroup.uid}
                    icon={getGroupIcon(unsortedGroup.uid)}
                >
                    {unsortedGroup.name}
                </Menu.Item>
            ),
        ].filter(Boolean);
    }, [filteredGroups, generateMenuItems]);

    return (
        <>
            <div
                style={{
                    width: 264,
                    padding: 12,
                    paddingTop: 24,
                    background: '#F1F1F1',
                    paddingBottom: 0,
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        paddingBottom: 16,
                        marginBottom: 12,
                        borderBottom: '1px solid var(--divider-2)',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span
                            className={'headline small'}
                            style={{ margin: 0, color: 'var(--gray-black)' }}
                        >
                            Группы
                        </span>
                        <span
                            className={'headline small'}
                            style={{ marginLeft: 8, color: '#999C9E' }}
                        >
                            ({flatGroups.length})
                        </span>
                    </div>
                    <ButtonAddPlus onClick={() => setIsModalNewGroupOpen(true)} />
                </div>

                <div style={{ display: 'flex', marginBottom: 12 }}>
                    <Input
                        placeholder="Поиск"
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ flex: 1, marginRight: 8 }}
                    />
                    <ButtonSettingGroup onClick={() => setIsModalSettingGroupOpen(true)} />
                </div>
            </div>
            <div>
                <ConfigProvider
                    theme={{
                        components: {
                            Menu: {
                                itemBg: '#F1F1F1',
                                itemHoverBg: '#E1E5E9',
                                itemSelectedBg: '#D3DADF',
                                subMenuItemBg: '#F1F1F1',
                            },
                        },
                    }}
                >
                    <Menu
                        mode="inline"
                        selectedKeys={[selectedGroup]}
                        openKeys={openKeys}
                        onOpenChange={setOpenKeys}
                        style={{ background: '#F1F1F1', borderRight: 0 }}
                        onClick={({ key }) => setSelectedGroup(key)}
                        expandIcon={({ isOpen }) => (isOpen ? <UpOutlined /> : <DownOutlined />)}
                    >
                        {menuItems}
                    </Menu>
                </ConfigProvider>

                <NewGroupModal
                    visible={isModalNewGroupOpen}
                    onCancel={() => setIsModalNewGroupOpen(false)}
                    onOk={() => {
                        setIsModalNewGroupOpen(false);
                        fetchGroups();
                    }}
                />

                <SettingGroupModal
                    visible={isModalSettingGroupOpen}
                    onOk={() => {
                        setIsModalSettingGroupOpen(false);
                        fetchGroups();
                    }}
                    onCancel={() => setIsModalSettingGroupOpen(false)}
                    onClose={handleSettingGroupDrawerClose}
                />
            </div>
        </>
    );
};

export default SideMenu;
